import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Gift, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Separator } from '@/common/ui/separator'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import useQueryGetDepositPromotionPresets from '../../api/hooks/useQueryGetDepositPromotionPresets'
import useMutationApplyDepositPromotionPreset from '../../api/hooks/useMutationApplyDepositPromotionPreset'
import type { DepositPromotionPreset } from '../../type'

function formatCreditType(preset: DepositPromotionPreset) {
	const { data } = preset
	if (data.creditType === 'RATIO' && data.creditRatio != null) {
		const pct = data.creditRatio * 100
		const cap =
			data.maxCreditAmount != null
				? ` (สูงสุด ${data.maxCreditAmount} บาท)`
				: ''
		return `โบนัส ${pct}%${cap}`
	}
	if (data.creditType === 'FIXED' && data.creditAmount != null) {
		return `โบนัสคงที่ ${data.creditAmount} บาท`
	}
	return '-'
}

function PresetCard({ preset }: { preset: DepositPromotionPreset }) {
	const navigate = useNavigate()
	const [showConfirm, setShowConfirm] = useState(false)
	const [showDescription, setShowDescription] = useState(false)
	const [name, setName] = useState(preset.name)

	const { mutateAsync, isPending } = useMutationApplyDepositPromotionPreset()

	const handleApply = async () => {
		try {
			const result = await mutateAsync({
				presetId: preset.id,
				payload: { name },
			})
			toast.success(`สร้างโปรโมชั่น "${result.data.name}" สำเร็จ`)
			setShowConfirm(false)
			navigate(`/operator/deposit-promotion/${result.data.id}`)
		} catch {
			toast.error('ไม่สามารถสร้างโปรโมชั่นจากเทมเพลตได้')
		}
	}

	const { data } = preset

	return (
		<>
			<Card className="flex flex-col transition-shadow hover:shadow-md">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-base">
						<Gift className="h-4 w-4 text-primary" />
						{preset.name}
					</CardTitle>
					<div className="flex flex-wrap gap-1.5">
						<Badge variant="outline" className="text-xs">
							เทิร์น {data.turnoverRound}x
						</Badge>
						{data.whitelistGameProviderNames.map((p: string) => (
							<Badge key={p} variant="secondary" className="text-xs">
								{p}
							</Badge>
						))}
					</div>
					<p className="text-sm font-medium text-foreground">
						{formatCreditType(preset)}
					</p>
				</CardHeader>

				<CardContent className="flex-1 space-y-2 pb-3">
					<button
						type="button"
						onClick={() => setShowDescription((v) => !v)}
						className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
					>
						{showDescription ? (
							<ChevronUp className="h-3 w-3" />
						) : (
							<ChevronDown className="h-3 w-3" />
						)}
						{showDescription ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
					</button>
					{showDescription && (
						<p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
							{preset.description}
						</p>
					)}
				</CardContent>

				<CardFooter>
					<Button
						className="w-full"
						onClick={() => setShowConfirm(true)}
						disabled={isPending}
					>
						<Sparkles className="mr-2 h-4 w-4" />
						ใช้เทมเพลตนี้
					</Button>
				</CardFooter>
			</Card>

			<AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันสร้างโปรโมชั่นจากเทมเพลต</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2">
								<p>
									จะสร้างโปรโมชั่นใหม่จากเทมเพลต &quot;{preset.name}&quot;
									โปรโมชั่นที่สร้างจะยังไม่ถูกเปิดใช้งาน
								</p>
								<p className="font-medium text-amber-600 dark:text-amber-400">
									โปรดตรวจสอบค่า RTP preset ก่อนเปิดใช้งานโมชั่น
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="space-y-2 py-2">
						<Label htmlFor="preset-name">ชื่อโปรโมชั่น</Label>
						<Input
							id="preset-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="ชื่อโปรโมชั่น"
						/>
					</div>
					<AlertDialogFooter className="gap-2">
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleApply}
							disabled={isPending || !name.trim()}
						>
							{isPending ? 'กำลังสร้าง...' : 'ยืนยัน'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

function PresetCardSkeleton() {
	return (
		<Card className="flex flex-col">
			<CardHeader className="pb-3">
				<Skeleton className="h-5 w-3/4" />
				<div className="flex gap-1.5">
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-20" />
				</div>
				<Skeleton className="h-4 w-1/2" />
			</CardHeader>
			<CardContent className="flex-1 pb-3">
				<Skeleton className="h-4 w-24" />
			</CardContent>
			<CardFooter>
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}

export default function DepositPromotionPresetGallery() {
	const { data, isLoading } = useQueryGetDepositPromotionPresets()
	const presets = data?.data ?? []

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-base font-semibold tracking-tight sm:text-lg">
					เทมเพลตโปรโมชั่น
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					เลือกเทมเพลตเพื่อสร้างโปรโมชั่นสำเร็จรูป
					<span className="font-medium text-amber-600 dark:text-amber-400">
						{' '}
						โปรดตรวจสอบค่า RTP preset ก่อนเปิดใช้งานโปรโมชั่น
					</span>
				</p>
			</div>
			<Separator />

			{isLoading ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<PresetCardSkeleton key={i} />
					))}
				</div>
			) : presets.length === 0 ? (
				<div className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบเทมเพลต</p>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{presets.map((preset: DepositPromotionPreset) => (
						<PresetCard key={preset.id} preset={preset} />
					))}
				</div>
			)}
		</div>
	)
}
